import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Field } from 'formik';
import styled from 'styled-components';
import { bp } from 'Styles/helpers';
import screen from 'superior-mq';
import { rgba, rem } from 'polished';
import { Router } from '../../../routes';
import RadioButtonGroup from '../../forms/radio-button-group';
import DonateCustom from '../../forms/donate-custom';
import DonateOption from '../../forms/donate-option';
import Button from '../../button';
import RadioInput from '../../forms/radio-input';
import Input from '../../forms/input';
import Grid from '../../grid';
import FieldGrid from '../../forms/radio-button-group/FieldGrid';

const amountGroupStyles = `
  grid-column: 4 / span 8;

  ${screen.below(bp.desktop, `
    grid-column: 3 / span 8;
  `)};

  ${screen.below(bp.laptopSm, `
    grid-column: 2 / span 8;
  `)};

  ${screen.below(bp.tablet, `
    grid-column: 1 / span 8;
  `)};

  ${screen.below(bp.portrait, `
    text-align: center;
    grid-column: 1 / -1;
  `)}
`;

const amountGroupStylesDockers = `
  ${amountGroupStyles}

  grid-column: 3 / span 8;
`;

const occurenceGroupAmount = `
  grid-column: 12 / -1;

  ${screen.below(bp.desktopMax, `
    grid-column: 13 / -1;
  `)};

  ${screen.below(bp.portrait, `
    margin-top: 20px;
    text-align: center;
    grid-column: 1 / -1;
  `)};
`;

const amountGridStyles = styled(FieldGrid)`
  ${screen.below(bp.portrait, `
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-gap: 5px;
    grid-auto-flow: row;
  `)};
`;

const amountGridStylesDockers = styled(FieldGrid)`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  grid-gap: 5px;
  grid-auto-flow: row;
`;

const occurenceGridStyles = styled(FieldGrid)`
  ${screen.below(bp.portrait, `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 5px;
    max-width: 400px;
    margin: auto;
  `)};
`;

const formFieldsStylesDocker = `
  ${screen.above('334px', `
    grid-column: span 4 / auto;
  `)}

  ${screen.below('335px', `
    grid-column: span 12 / auto;
  `)}
`;

const amountGroupStylesTumi = `
  ${amountGroupStyles}

  ${screen.above(bp.laptopSm, `
    grid-column: 3 / span 4;
  `)};

  ${screen.below(bp.laptopSm, `
    grid-column: 2 / span 4;
  `)};

  ${screen.below(bp.tablet, `
    text-align: center;
    grid-column: 1 / -1;
  `)};
`;

const selectStylesTumi = `
  grid-column: 8 / span 3;
  padding-bottom: 1rem;
  align-self: flex-start;

  ${screen.below(bp.desktop, `
    grid-column: 7 / span 5;
    justify-self: center;
  `)};

  ${screen.below(bp.tablet, `
    margin-top: 75px;
    grid-column: 1 / span 6;
  `)};

  ${screen.below(bp.mobile, `
    margin-top: 45px;
    text-align: center;
    grid-column: 1 / -1;
  `)}
`;

const inputStylesTumi = `
  color: #fff;
  margin: 0;
`;

const InputTumi = styled.div`
  position: relative;
  grid-column: 12 / -1;
  align-self: flex-end;
  padding: 0;

  div {
    text-align: right;
  }

  ${screen.below(bp.desktop, `
    grid-column: 13 / -1;
  `)};

  ${screen.below(bp.tablet, `
    margin-top: 75px;
    grid-column: 8 / -1;
  `)};

  ${screen.below(bp.mobile, `
    text-align: center;
    grid-column: 1 / -1;
    margin-top: 25px;
  `)}
`;

const LabelTumi = styled.label`
  display: block;
  line-height: 1.7;
  width: 100%;
  margin-bottom: 20px;
  font-size: ${rem(16)};
  color: ${props => props.isDark ? '#808080;' : rgba(props.theme.blue, 0.75)};
`;

const MessageTumi = styled.span`
  display: block;
  font-size: ${rem(12)};
  position: absolute;
  bottom: .75rem;
  left: 0;
  line-height: 1;
`;

const validateOptions = (e, values, changeFunc, setFieldValue) => {
  changeFunc(e);

  if (values.customAmount.length > 0) {
    setFieldValue('customAmount', '');
  }
};

const ProgramDonate = ({ id, isDockers = false, isTumi = false, isDark = false, buttonIsDark }) => (
  <Formik
    initialValues={{
      amount: isTumi ? '' : '25',
      customAmount: isTumi ? '60' : '',
      frequency: '1',
      tumiFounded: '',
      tumiCount: '',
    }}
    onSubmit={(values, actions) => {
      if (values.customAmount.length) {
        values.customAmount = values.customAmount.replace(/\$/, '').replace(/,/gm, '');
      }

      sessionStorage.setItem(`donate-${id}`, JSON.stringify(values));
      actions.setSubmitting(false);
      Router.push(`/donate/${id}`);
    }}
    validate={(values) => {
      const errors = {};

      if (values.customAmount && !/(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/.test(values.customAmount)) {
        errors.customAmount = 'Enter a valid number';
      }

      if (isTumi) {
        if (values.tumiFounded === '') {
          errors.tumiFounded = 'Select which year Tumi was founded';
        }
        if (values.tumiCount === '') {
          errors.tumiCount = 'Enter a participant count';
        }
      }

      return errors;
    }}
    render={({
      handleSubmit,
      setFieldValue,
      handleChange,
      handleBlur,
      values,
      errors,
      touched,
      isSubmitting,
    }) => (
      <form onSubmit={handleSubmit} autoComplete="off">
        <Grid cols="17" styles="text-align: left; grid-gap: 0;">

          {(!isDockers && !isTumi) &&
            <RadioButtonGroup
              id="amount"
              label="Donation Amount"
              value={values.amount}
              error={errors.amount}
              touched={touched.amount}
              styles={amountGroupStyles}
              gridComponent={amountGridStyles}
              isDark={isDark}
            >
              <Field
                component={DonateOption}
                name="amount"
                id="25"
                label="$25"
                onChange={(e) => { validateOptions(e, values, handleChange, setFieldValue); }}
              />
              <Field
                component={DonateOption}
                name="amount"
                id="50"
                label="$50"
                onChange={(e) => { validateOptions(e, values, handleChange, setFieldValue); }}
              />
              <Field
                component={DonateOption}
                name="amount"
                id="100"
                label="$100"
                onChange={(e) => { validateOptions(e, values, handleChange, setFieldValue); }}
              />
              <Field
                component={DonateOption}
                name="amount"
                id="1000"
                label="$1,000"
                onChange={(e) => { validateOptions(e, values, handleChange, setFieldValue); }}
              />

              <OtherAmount
                name="customAmount"
                label="Other Amount"
                placeholder="Other Amount"
                pattern="[0-9]*"
                onChange={(e) => {
                  handleChange(e);
                  if (e.target.value !== '') {
                    setFieldValue('amount', '');
                  }
                }}
                onBlur={handleBlur}
                value={values.customAmount}
                errors={errors.customAmount && touched.customAmount && errors.customAmount}
              />
            </RadioButtonGroup>
          }

          {isTumi &&
            <React.Fragment>
              <RadioButtonGroup
                id="amount"
                label="One filter, plus implementation costs, equal €50 (fifty euros)"
                value={values.amount}
                error={errors.amount}
                touched={touched.amount}
                styles={amountGroupStylesTumi}
                isDark={isDark}
              >
                <Field
                  component={DonateOption}
                  name="amount"
                  id="60"
                  label="1 Filter"
                  onChange={(e) => { validateOptions(e, values, handleChange, setFieldValue); }}
                  checked
                />
              </RadioButtonGroup>
              <RadioButtonGroup
                id="tumiFounded"
                label="What year was Tumi founded?"
                value={values.tumiFounded}
                error={errors.tumiFounded}
                touched={touched.tumiFounded}
                styles={selectStylesTumi}
                isDark={isDark}
              >
                <Field
                  component={DonateOption}
                  name="tumiFounded"
                  id="1975"
                  label="1975"
                  onChange={handleChange}
                />
                <Field
                  component={DonateOption}
                  name="tumiFounded"
                  id="2000"
                  label="2000"
                  onChange={handleChange}
                />
                <Field
                  component={DonateOption}
                  name="tumiFounded"
                  id="2010"
                  label="2010"
                  onChange={handleChange}
                />
              </RadioButtonGroup>

              <InputTumi>
                <LabelTumi htmlFor="tumiCount">How many different participants do you think there will be in this contest from 15/11/2020 (12:00am) until 25/12/2020 (12:00am)?</LabelTumi>
                <Input
                  type="text"
                  name="tumiCount"
                  placeholder="Your Answer"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.tumiCount}
                  errors={errors.tumiCount && touched.tumiCount && errors.tumiCount}
                  styles={inputStylesTumi}
                  isDark
                  required
                />
                <MessageTumi>* denotes required field</MessageTumi>
              </InputTumi>
            </React.Fragment>
          }

          {isDockers &&
            <RadioButtonGroup
              id="amount"
              label="Number of Filters (1 Filter costs $35)"
              value={values.amount}
              error={errors.amount}
              touched={touched.amount}
              styles={amountGroupStylesDockers}
              gridComponent={amountGridStylesDockers}
              isDark={isDark}
            >
              <Field
                component={DonateOption}
                name="amount"
                id="35"
                label="1 Filter"
                onChange={(e) => { validateOptions(e, values, handleChange, setFieldValue); }}
                styles={formFieldsStylesDocker}
              />
              <Field
                component={DonateOption}
                name="amount"
                id="175"
                label="5 Filters"
                onChange={(e) => { validateOptions(e, values, handleChange, setFieldValue); }}
                styles={formFieldsStylesDocker}
              />
              <Field
                component={DonateOption}
                name="amount"
                id="350"
                label="10 Filters"
                onChange={(e) => { validateOptions(e, values, handleChange, setFieldValue); }}
                styles={formFieldsStylesDocker}
              />

              <OtherAmount
                name="customAmount"
                label="Custom Dollar Amount"
                placeholder="Custom Dollar Amount"
                pattern="[0-9]*"
                onChange={(e) => {
                  handleChange(e);
                  if (e.target.value !== '') {
                    setFieldValue('amount', '');
                  }
                }}
                onBlur={handleBlur}
                value={values.customAmount}
                errors={errors.customAmount && touched.customAmount && errors.customAmount}
                isdockers={isDockers ? 'true' : 'false'}
                wrapStyles="grid-column: 1 / span 12;"
              />
            </RadioButtonGroup>
          }

          {!isTumi &&
            <RadioButtonGroup
              id="frequency"
              label="Donation Occurence"
              value={values.frequency}
              error={errors.frequency}
              touched={touched.frequency}
              styles={occurenceGroupAmount}
              gridFlow="row"
              gridComponent={occurenceGridStyles}
              isDark={isDark}
            >
              <Field
                component={RadioInput}
                name="frequency"
                id="1"
                label="One Time Donation"
              />

              <Field
                component={RadioInput}
                name="frequency"
                id="0"
                label="Recurring Donation"
              />
            </RadioButtonGroup>
          }
        </Grid>
        <SubmitDonation
          type="submit"
          disabled={isSubmitting || (!values.amount && !values.customAmount) || errors.customAmount}
          isdark={buttonIsDark}
          formNoValidate="formnovalidate"
        >
          Donate
        </SubmitDonation>
      </form>
    )}
  />
);

const SubmitDonation = styled(Button)`
  margin-top: 40px;
`;

const OtherAmount = styled(DonateCustom)`
  max-width: 190px;

  ${props => props.isdockers && `
    width: 100%;
    max-width: 100%;
  `}

  ${screen.below(bp.portrait, `
    width: 100%;
    max-width: 100%;
  `)};
`;

ProgramDonate.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isDockers: PropTypes.bool,
  isDark: PropTypes.bool,
  buttonIsDark: PropTypes.bool,
};

export default ProgramDonate;
